import { SelectOption } from 'global';

import { DealSheetFormValues } from '../ui/rule/types';
import { DealSheetRulesTableRow } from '../ui/table/types';

class DealSheetConfigurationMapper {
  processSalesCenterFormToAPIDto(formData: DealSheetFormValues): Record<string, any> {
    const charges = formData.charges.map(this.chargeToAPIDto.bind(this));
    const taxes = formData.taxes.map(this.taxToAPIDto.bind(this));

    return {
      charges: [...charges, ...taxes],

      name: formData.name,
      ...(formData.description ? { description: formData.description } : {}),

      dealRules: {
        ...(formData.equipment.type.length
          ? {
              equipment: {
                type: formData.equipment.type,
              },
            }
          : {}),

        ...(Object.values(formData.customer).some(value => !!value.length)
          ? {
              customer: {
                ...(formData?.customer?.type?.length
                  ? {
                      type: formData.customer.type,
                    }
                  : {}),
                ...(formData?.customer?.paymentType?.length
                  ? {
                      paymentType: formData.customer.paymentType,
                    }
                  : {}),
              },
            }
          : {}),

        ...(Object.values(formData?.address)
          .filter(value => Array.isArray(value))
          .some(value => !!value.length)
          ? {
              location: {
                country: formData.address.country,

                ...(formData?.address?.zip?.length
                  ? {
                      zip: formData.address.zip,
                    }
                  : {}),

                ...(formData?.address?.city?.length
                  ? {
                      city: formData.address.city,
                    }
                  : {}),

                ...(formData?.address?.postalCode?.length
                  ? {
                      postalCode: formData.address.postalCode,
                    }
                  : {}),

                ...(formData?.address?.province?.length
                  ? {
                      province: formData.address.province,
                    }
                  : {}),

                ...(formData?.address?.county?.length
                  ? {
                      county: formData.address.county,
                    }
                  : {}),

                ...(formData?.address?.state?.length
                  ? {
                      state: formData.address.state,
                    }
                  : {}),
              },
            }
          : {}),
      },
    };
  }

  private chargeToAPIDto(charge: DealSheetFormValues['charges'][number]) {
    return {
      name: charge.name,
      type: charge.type,
      equipmentId: null,
      amount: charge.amount,
      isTaxable: charge.isTaxable,
      amountType: charge.amountType,
      description: charge.description,
    };
  }

  private taxToAPIDto(tax: DealSheetFormValues['taxes'][number]) {
    return {
      name: tax.name,
      type: tax.type,
      equipmentId: null,
      amount: tax.amount,
      amountType: tax.amountType,
      description: tax.description,
    };
  }

  public processDealSheetToTable(
    tableData: Record<string, any>[],
    {
      countries,
      usRegions,
      canadaRegions,
    }: {
      usRegions: SelectOption[];
      countries: SelectOption[];
      canadaRegions: SelectOption[];
    }
  ): DealSheetRulesTableRow[] {
    return tableData.map(rule => ({
      id: rule.id,
      name: rule.name,
      description: rule.description,
      createdDateTime: rule.createdDateTime,

      propCustomerType: rule.dealRules.customer?.type,
      propCustomerPaymentType: rule.dealRules.customer?.paymentType,

      propLocationZip: rule.dealRules.location?.zip,
      propLocationCity: rule.dealRules.location?.city,
      propEquipmentType: rule.dealRules.equipment?.type,
      propLocationCounty: rule.dealRules.location?.county,
      propLocationPostalCode: rule.dealRules.location?.postalCode,
      propLocationState: rule.dealRules.location?.state?.map(
        (stateValue: any) => usRegions.find(({ value }) => value === stateValue)?.label ?? stateValue
      ),
      propLocationCountry: rule.dealRules.location?.country?.map(
        (countryValue: any) => countries.find(({ value }) => value === countryValue)?.label ?? countryValue
      ),
      propLocationProvince: rule.dealRules.location?.province?.map(
        (provinceValue: any) => canadaRegions.find(({ value }) => value === provinceValue)?.label ?? provinceValue
      ),
    }));
  }
}

export const dealSheetConfigurationMapper = new DealSheetConfigurationMapper();
